import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { Logo } from "../components/Logo";
import { CheckCircle2, AlertTriangle, Sparkles, ChevronRight, ChevronLeft, Info } from "lucide-react";

/*
 * BILAN SUCCESSION — Page publique /bilan-succession
 * Quiz d'auto-évaluation (15 questions · 3 piliers) + coordonnées + choix expert + résultat immédiat
 * Header minimaliste (logo seul) pour maximiser la conversion.
 */

const QUESTIONS = {
  admin: {
    label: "Organisation administrative",
    intro: "Vos documents et accès essentiels sont-ils en ordre ?",
    items: [
      { key: "admin_q1", label: "Vos documents d'identité (actes de naissance, livrets) sont-ils à jour et accessibles ?" },
      { key: "admin_q2", label: "Pouvez-vous localiser vos actes de propriété et titres de transport en moins de 5 minutes ?" },
      { key: "admin_q3", label: "Avez-vous un inventaire écrit de vos biens mobiliers (bijoux, art, collections) ?" },
      { key: "admin_q4", label: "Vos accès numériques (codes banques, impôts, mails) sont-ils sécurisés mais accessibles à vos proches ?" },
      { key: "admin_q5", label: "Vos proches savent-ils exactement où trouver ces documents en cas d'urgence ?" },
    ],
  },
  juridique: {
    label: "Protection juridique & volontés",
    intro: "Vos volontés et la protection de vos proches sont-elles écrites et sécurisées ?",
    items: [
      { key: "juridique_q6", label: "Avez-vous rédigé un testament enregistré (notaire ou FCDDV) ?" },
      { key: "juridique_q7", label: "Avez-vous désigné une personne de confiance ou un exécuteur testamentaire ?" },
      { key: "juridique_q8", label: "Avez-vous anticipé une éventuelle dépendance via un mandat de protection future ?", info_on_non: "Le saviez-vous ? Sans mandat de protection future, c'est le juge qui décide pour vous en cas d'accident ou de perte d'autonomie." },
      { key: "juridique_q9", label: "Connaissez-vous précisément les droits de vos héritiers selon votre situation (PACS, union libre, etc.) ?" },
      { key: "juridique_q10", label: "Avez-vous clairement exprimé vos volontés funéraires ?" },
    ],
  },
  financier: {
    label: "Optimisation financière & fiscale",
    intro: "Votre patrimoine et sa fiscalité sont-ils réellement optimisés ?",
    items: [
      { key: "financier_q11", label: "Avez-vous actualisé la valeur de votre patrimoine global ces 12 derniers mois ?" },
      { key: "financier_q12", label: "Les clauses bénéficiaires de vos assurances-vie ont-elles été révisées récemment ?" },
      { key: "financier_q13", label: "Avez-vous mis en place des stratégies de transmission de votre vivant (donations, démembrement) ?" },
      { key: "financier_q14", label: "Vos dettes et engagements financiers sont-ils listés et connus ?" },
      { key: "financier_q15", label: "Avez-vous déjà consulté un expert (notaire, CGP) pour optimiser les droits de succession ?" },
    ],
  },
};

const EXPERTS = [
  { key: "notaire", label: "Le Notaire", description: "Indispensable pour rédiger, enregistrer et sécuriser juridiquement votre testament ou vos actes de donation. Il est le garant du respect des règles successorales et de la validité de vos volontés devant la loi." },
  { key: "agent_immobilier", label: "L'Agent immobilier", description: "Idéal si votre patrimoine est majoritairement composé de biens immobiliers. Il estime précisément la valeur de vos actifs pour éviter les litiges entre héritiers et vous conseille sur la mise en vente ou la gestion locative." },
  { key: "avocat", label: "L'Avocat", description: "Expert en stratégie juridique, il vous accompagne dans les situations familiales complexes (familles recomposées, protection du conjoint, litiges potentiels) pour anticiper et prévenir tout conflit futur." },
  { key: "coordinateur_auprea", label: "Le Coordinateur AUPRÉA", description: "Votre chef d'orchestre. Il vous aide à rassembler vos documents, organise votre inventaire et assure le lien entre tous les professionnels. C'est l'interlocuteur privilégié pour une mise en place sereine et structurée." },
];

const POINTS = { oui: 2, partiel: 1, non: 0 };

function computeScore(reponses) {
  const pillar = (keys) => keys.reduce((s, k) => s + (POINTS[reponses[k]] ?? 0), 0);
  const admin = pillar(QUESTIONS.admin.items.map((q) => q.key));
  const juridique = pillar(QUESTIONS.juridique.items.map((q) => q.key));
  const financier = pillar(QUESTIONS.financier.items.map((q) => q.key));
  const total = admin + juridique + financier;
  let diagnostic = "en_cours";
  if (total <= 10) diagnostic = "urgence";
  else if (total >= 21) diagnostic = "serein";
  return { admin, juridique, financier, total, diagnostic };
}

const DIAGNOSTIC_META = {
  urgence: { label: "Urgence critique", color: "#b53a3a", icon: AlertTriangle,
    message: "Votre succession présente des zones de risques majeures. Vos proches pourraient se retrouver dans une situation complexe. Une structuration immédiate est recommandée." },
  en_cours: { label: "En cours de sécurisation", color: "#caa86a", icon: Sparkles,
    message: "Vous avez les bases, mais des failles juridiques ou fiscales subsistent. Un accompagnement permettrait de verrouiller votre stratégie." },
  serein: { label: "Sérénité totale", color: "#2f7d5b", icon: CheckCircle2,
    message: "Bravo, tout est sous contrôle. Un simple point de suivi régulier suffit pour maintenir cette organisation." },
};

const REGISTER_URL = "https://www.auprea.com/inscription";

export default function BilanSuccession() {
  // Étapes : 0=admin, 1=juridique, 2=financier, 3=coordonnées, 4=expert, 5=résultat
  const [step, setStep] = useState(0);
  const [reponses, setReponses] = useState({});
  const [coords, setCoords] = useState({ prenom: "", nom: "", annee_naissance: "", email: "", telephone: "" });
  const [expert, setExpert] = useState("");
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [result, setResult] = useState(null); // { id, score_total, score_admin, ..., diagnostic }
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [callbackSent, setCallbackSent] = useState(false);
  const [callbackSubmitting, setCallbackSubmitting] = useState(false);

  useEffect(() => {
    if (typeof document !== "undefined") document.title = "Bilan Succession · AUPRÉA";
  }, []);
  useEffect(() => {
    // scroll top on step change
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const pillarKeys = ["admin", "juridique", "financier"];
  const isQuestionStep = step >= 0 && step <= 2;

  const currentPillar = isQuestionStep ? QUESTIONS[pillarKeys[step]] : null;

  const canGoNext = useMemo(() => {
    if (isQuestionStep) {
      return currentPillar.items.every((q) => ["oui", "partiel", "non"].includes(reponses[q.key]));
    }
    if (step === 3) {
      const emailOk = /\S+@\S+\.\S+/.test(coords.email);
      const yr = parseInt(coords.annee_naissance, 10);
      return coords.prenom.trim() && coords.nom.trim() && yr >= 1900 && yr <= new Date().getFullYear() && emailOk;
    }
    if (step === 4) return !!expert;
    return true;
  }, [step, isQuestionStep, currentPillar, reponses, coords, expert]);

  const progressPct = Math.round(((step + 1) / 5) * 100);

  const goNext = async () => {
    if (!canGoNext) return;
    if (step < 4) {
      setStep(step + 1);
      return;
    }
    // step === 4 → submit
    setSubmitting(true);
    setSubmitError("");
    // Calcul client-side (résilient réseau)
    const localScore = computeScore(reponses);
    try {
      const { data } = await api.post("/public/bilan-succession/submit", {
        prenom: coords.prenom.trim(),
        nom: coords.nom.trim(),
        annee_naissance: parseInt(coords.annee_naissance, 10),
        email: coords.email.trim(),
        telephone: coords.telephone.trim(),
        reponses,
        expert_prioritaire: expert,
        consent_marketing: consentMarketing,
        consent_date: consentMarketing ? new Date().toISOString() : null,
      });
      setResult({
        id: data.id,
        score_admin: data.score_admin,
        score_juridique: data.score_juridique,
        score_financier: data.score_financier,
        score_total: data.score_total,
        diagnostic: data.diagnostic,
        expert: data.expert,
      });
      setStep(5);
    } catch (_err) {
      // Fallback : affichage local même si le POST échoue
      setResult({
        id: null,
        ...localScore,
        expert: EXPERTS.find((e) => e.key === expert),
      });
      setStep(5);
      setSubmitError("Votre bilan est affiché, mais un souci réseau a empêché son enregistrement. Il sera automatiquement retransmis.");
      // Retry silencieux via localStorage
      try {
        const payload = { prenom: coords.prenom, nom: coords.nom, annee_naissance: parseInt(coords.annee_naissance, 10), email: coords.email, telephone: coords.telephone, reponses, expert_prioritaire: expert, consent_marketing: consentMarketing, consent_date: consentMarketing ? new Date().toISOString() : null };
        localStorage.setItem("bilan_pending_retry", JSON.stringify(payload));
      } catch (_e) { /* ignore */ }
    } finally {
      setSubmitting(false);
    }
  };

  const requestCallback = async () => {
    if (!result?.id || callbackSent || callbackSubmitting) return;
    setCallbackSubmitting(true);
    try {
      await api.post(`/public/bilan-succession/${result.id}/request-callback`);
      setCallbackSent(true);
    } catch (_e) {
      // On considère la demande comme envoyée côté UX pour ne pas frustrer
      setCallbackSent(true);
    } finally {
      setCallbackSubmitting(false);
    }
  };

  // Silent retry effect (network came back)
  useEffect(() => {
    const retry = async () => {
      try {
        const pending = localStorage.getItem("bilan_pending_retry");
        if (!pending) return;
        const payload = JSON.parse(pending);
        const { data } = await api.post("/public/bilan-succession/submit", payload);
        if (data?.id && result && !result.id) {
          setResult((r) => ({ ...r, id: data.id }));
        }
        localStorage.removeItem("bilan_pending_retry");
      } catch (_e) { /* ignore */ }
    };
    if (step === 5) retry();
  }, [step, result]);

  return (
    <div style={{ backgroundColor: "var(--auprea-cream)", minHeight: "100vh" }} data-testid="bilan-page">
      {/* Header minimaliste : logo seul, pas de menu */}
      <nav className="flex items-center justify-center px-6 py-6 border-b" style={{ borderColor: "rgba(21,41,79,0.1)" }}>
        <Link to="/" data-testid="bilan-logo-home">
          <Logo size={28} />
        </Link>
      </nav>

      {/* Barre de progression */}
      {step <= 4 && (
        <div className="max-w-3xl mx-auto px-6 pt-8" data-testid="bilan-progress">
          <div className="flex items-center justify-between mb-2" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(35,38,46,0.55)" }}>
            <span>Étape {step + 1} / 5</span>
            <span>{progressPct} %</span>
          </div>
          <div style={{ height: 4, background: "rgba(21,41,79,0.08)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${progressPct}%`, height: "100%", background: "var(--auprea-gold)", transition: "width 0.4s ease" }} />
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        {/* Intro seulement à l'étape 0 */}
        {step === 0 && (
          <div className="mb-10 text-center" data-testid="bilan-intro">
            <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>— Bilan Succession</div>
            <h1 style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', color: "var(--auprea-navy)", fontSize: "clamp(28px, 4.6vw, 44px)", fontWeight: 500, lineHeight: 1.15, margin: 0 }}>
              Où en êtes-vous vraiment&nbsp;?
            </h1>
            <p style={{ fontSize: 17, color: "rgba(35,38,46,0.75)", marginTop: 18, lineHeight: 1.6 }}>
              En 15 questions, découvrez votre score de préparation successorale, votre diagnostic personnalisé,
              et l'expert le plus adapté à votre situation. Réservé aux personnes soucieuses de protéger leurs proches.
            </p>
            <div style={{ marginTop: 22, fontSize: 13, color: "rgba(35,38,46,0.55)", fontStyle: "italic" }}>« Anticiper est un acte d'amour. »</div>
          </div>
        )}

        {/* Étape questions */}
        {isQuestionStep && (
          <div data-testid={`bilan-step-${pillarKeys[step]}`}>
            <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10 }}>
              Pilier {step + 1} / 3
            </div>
            <h2 style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', color: "var(--auprea-navy)", fontSize: 28, fontWeight: 500, margin: 0 }}>
              {currentPillar.label}
            </h2>
            <p style={{ fontSize: 15, color: "rgba(35,38,46,0.7)", margin: "10px 0 32px 0" }}>{currentPillar.intro}</p>

            <div className="space-y-6">
              {currentPillar.items.map((q, idx) => (
                <div key={q.key} data-testid={`q-${q.key}`}>
                  <div style={{ fontSize: 15, color: "var(--auprea-ink)", marginBottom: 12, lineHeight: 1.5 }}>
                    <strong style={{ color: "var(--auprea-navy)", marginRight: 6 }}>{idx + 1}.</strong> {q.label}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: "oui", lbl: "Oui" },
                      { v: "partiel", lbl: "Partiellement" },
                      { v: "non", lbl: "Non" },
                    ].map((opt) => {
                      const sel = reponses[q.key] === opt.v;
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => setReponses((r) => ({ ...r, [q.key]: opt.v }))}
                          data-testid={`answer-${q.key}-${opt.v}`}
                          style={{
                            padding: "12px 8px",
                            border: `1px solid ${sel ? "var(--auprea-gold)" : "rgba(21,41,79,0.15)"}`,
                            background: sel ? "rgba(202,168,106,0.12)" : "#fff",
                            color: sel ? "var(--auprea-navy)" : "var(--auprea-ink)",
                            fontSize: 14,
                            fontWeight: sel ? 600 : 500,
                            cursor: "pointer",
                            borderRadius: 4,
                            transition: "all 0.15s ease",
                          }}
                        >
                          {opt.lbl}
                        </button>
                      );
                    })}
                  </div>
                  {q.info_on_non && reponses[q.key] === "non" && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: "12px 14px",
                        background: "rgba(202,168,106,0.12)",
                        borderLeft: "3px solid var(--auprea-gold)",
                        fontSize: 13,
                        color: "var(--auprea-navy)",
                        lineHeight: 1.5,
                      }}
                      data-testid={`info-on-non-${q.key}`}
                    >
                      <Info size={14} style={{ display: "inline-block", marginRight: 6, verticalAlign: "-2px" }} />
                      {q.info_on_non}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Étape coordonnées */}
        {step === 3 && (
          <div data-testid="bilan-step-coords">
            <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10 }}>
              Presque terminé
            </div>
            <h2 style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', color: "var(--auprea-navy)", fontSize: 28, fontWeight: 500, margin: 0 }}>
              Vos coordonnées
            </h2>
            <p style={{ fontSize: 14, color: "rgba(35,38,46,0.7)", margin: "8px 0 28px 0" }}>
              Nécessaires pour vous envoyer votre bilan par email. Vos données restent strictement confidentielles.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Prénom *" testid="input-prenom" value={coords.prenom} onChange={(v) => setCoords((c) => ({ ...c, prenom: v }))} />
              <Field label="Nom *" testid="input-nom" value={coords.nom} onChange={(v) => setCoords((c) => ({ ...c, nom: v }))} />
              <Field label="Année de naissance *" testid="input-annee" placeholder="Ex. 1965" value={coords.annee_naissance} onChange={(v) => setCoords((c) => ({ ...c, annee_naissance: v.replace(/[^0-9]/g, "").slice(0, 4) }))} inputMode="numeric" />
              <Field label="Email *" testid="input-email" type="email" value={coords.email} onChange={(v) => setCoords((c) => ({ ...c, email: v }))} />
              <div className="md:col-span-2">
                <Field label="Téléphone (facultatif)" testid="input-tel" value={coords.telephone} onChange={(v) => setCoords((c) => ({ ...c, telephone: v }))} placeholder="06 12 34 56 78" />
              </div>
            </div>
            <p style={{ fontSize: 12, color: "rgba(35,38,46,0.5)", marginTop: 20, lineHeight: 1.5 }}>
              En soumettant ce formulaire, vous acceptez que vos données soient traitées par AUPRÉA
              pour vous envoyer votre bilan et éventuellement vous recontacter. Conformément au RGPD,
              vous pouvez à tout moment demander la suppression de vos données à{" "}
              <a href="mailto:contact@auprea.com" style={{ color: "var(--auprea-blue)" }}>contact@auprea.com</a>.
            </p>
            <label data-testid="bilan-field-consent" style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 14px", marginTop: 16, background: "rgba(202,168,106,0.08)", border: "1px solid rgba(202,168,106,0.35)", borderRadius: 4, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={consentMarketing}
                onChange={(e) => setConsentMarketing(e.target.checked)}
                data-testid="bilan-input-consent"
                style={{ marginTop: 3, accentColor: "var(--auprea-gold)", cursor: "pointer", flexShrink: 0 }}
              />
              <span style={{ fontSize: 13.5, color: "var(--auprea-ink)", lineHeight: 1.55 }}>
                J'accepte de recevoir des conseils et des rappels d'AUPRÉA par email pour avancer sur ma succession. Je peux me désinscrire à tout moment.
              </span>
            </label>
          </div>
        )}

        {/* Étape expert */}
        {step === 4 && (
          <div data-testid="bilan-step-expert">
            <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10 }}>
              Dernière étape
            </div>
            <h2 style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', color: "var(--auprea-navy)", fontSize: 28, fontWeight: 500, margin: 0 }}>
              Quel expert souhaitez-vous en priorité&nbsp;?
            </h2>
            <p style={{ fontSize: 14, color: "rgba(35,38,46,0.7)", margin: "8px 0 28px 0" }}>
              Choisissez le profil qui correspond le mieux à votre situation actuelle.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {EXPERTS.map((e) => {
                const sel = expert === e.key;
                return (
                  <button
                    key={e.key}
                    type="button"
                    onClick={() => setExpert(e.key)}
                    data-testid={`expert-${e.key}`}
                    style={{
                      textAlign: "left",
                      padding: 20,
                      border: `2px solid ${sel ? "var(--auprea-gold)" : "rgba(21,41,79,0.12)"}`,
                      background: sel ? "rgba(202,168,106,0.08)" : "#fff",
                      cursor: "pointer",
                      borderRadius: 6,
                      transition: "all 0.15s ease",
                    }}
                  >
                    <div style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', fontSize: 20, color: "var(--auprea-navy)", fontWeight: 500, marginBottom: 8 }}>
                      {e.label}
                    </div>
                    <div style={{ fontSize: 14, color: "rgba(35,38,46,0.7)", lineHeight: 1.55 }}>{e.description}</div>
                  </button>
                );
              })}
            </div>
            {expert && (
              <div
                style={{
                  marginTop: 24,
                  padding: "20px 22px",
                  background: "var(--auprea-navy)",
                  color: "rgba(243,239,230,0.95)",
                  borderRadius: 6,
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
                data-testid="bon-a-savoir"
              >
                <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>Bon à savoir</div>
                Sur la plateforme AUPRÉA, retrouvez notaires, avocats, agents immobiliers et coordinateurs vérifiés,
                tous réunis au même endroit pour vous accompagner.
                <div style={{ marginTop: 14 }}>
                  <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" data-testid="cta-inscription-expert" style={{ display: "inline-block", padding: "10px 20px", background: "var(--auprea-gold)", color: "var(--auprea-navy)", textDecoration: "none", fontWeight: 600, fontSize: 14, borderRadius: 4, letterSpacing: "0.03em" }}>
                    Créer mon compte gratuit sur AUPRÉA
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Étape résultat */}
        {step === 5 && result && (
          <ResultBlock
            result={result}
            expertObj={result.expert || EXPERTS.find((e) => e.key === expert)}
            callbackSent={callbackSent}
            callbackSubmitting={callbackSubmitting}
            onRequestCallback={requestCallback}
            submitError={submitError}
          />
        )}

        {/* Navigation */}
        {step <= 4 && (
          <div className="flex items-center justify-between mt-12 gap-3" data-testid="bilan-nav">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} className="auprea-btn-outline" style={{ padding: "12px 22px" }} data-testid="btn-prev">
                <ChevronLeft size={16} style={{ marginRight: 6 }} /> Précédent
              </button>
            ) : <span />}
            <button
              onClick={goNext}
              disabled={!canGoNext || submitting}
              data-testid="btn-next"
              style={{
                padding: "14px 28px",
                background: canGoNext ? "var(--auprea-navy)" : "rgba(21,41,79,0.35)",
                color: "#fff",
                border: "none",
                fontWeight: 600,
                fontSize: 15,
                borderRadius: 4,
                cursor: canGoNext ? "pointer" : "not-allowed",
                letterSpacing: "0.03em",
                transition: "background 0.15s ease",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {submitting ? "Traitement…" : (step === 4 ? "Voir mon diagnostic" : "Suivant")}
              {!submitting && <ChevronRight size={16} />}
            </button>
          </div>
        )}
      </main>

      <footer className="text-center py-8 border-t" style={{ borderColor: "rgba(21,41,79,0.08)", color: "rgba(35,38,46,0.55)", fontSize: 12 }}>
        AUPRÉA · <em>Anticiper est un acte d'amour.</em>
      </footer>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", placeholder = "", inputMode }) {
  return (
    <div>
      <label style={{ fontSize: 11, color: "rgba(35,38,46,0.7)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, display: "block", marginBottom: 6 }}>{label}</label>
      <input
        className="auprea-input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        data-testid={testid}
        style={{ width: "100%", background: "#fff" }}
      />
    </div>
  );
}

function ResultBlock({ result, expertObj, callbackSent, callbackSubmitting, onRequestCallback, submitError }) {
  const meta = DIAGNOSTIC_META[result.diagnostic] || DIAGNOSTIC_META.en_cours;
  const Icon = meta.icon;
  return (
    <div data-testid="bilan-step-result">
      {submitError && (
        <div style={{ marginBottom: 20, padding: "12px 16px", background: "rgba(181,58,58,0.08)", border: "1px solid rgba(181,58,58,0.25)", color: "#b53a3a", fontSize: 13, borderRadius: 4 }} data-testid="submit-warning">
          {submitError}
        </div>
      )}

      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>Votre bilan personnalisé</div>
        <div style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', color: "var(--auprea-navy)", fontSize: 64, fontWeight: 500, lineHeight: 1 }} data-testid="score-total">
          {result.score_total} <span style={{ fontSize: 30, color: "rgba(35,38,46,0.4)" }}>/ 30</span>
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            marginTop: 16,
            padding: "8px 20px",
            background: meta.color,
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            borderRadius: 999,
          }}
          data-testid={`badge-diagnostic-${result.diagnostic}`}
        >
          <Icon size={16} /> {meta.label}
        </div>
        <p style={{ maxWidth: 560, margin: "22px auto 0", fontSize: 15, color: "rgba(35,38,46,0.75)", lineHeight: 1.6 }} data-testid="diagnostic-message">
          {meta.message}
        </p>
      </div>

      {/* Détail piliers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8" data-testid="pillar-scores">
        <PillarBar label="Organisation administrative" value={result.score_admin} />
        <PillarBar label="Protection juridique &amp; volontés" value={result.score_juridique} />
        <PillarBar label="Optimisation financière &amp; fiscale" value={result.score_financier} />
      </div>

      {/* Expert récap */}
      {expertObj && (
        <div style={{ background: "var(--auprea-navy)", color: "#f3efe6", padding: "22px 24px", borderRadius: 6, marginBottom: 28 }} data-testid="expert-recap">
          <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>Votre expert prioritaire</div>
          <div style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', fontSize: 22, fontWeight: 500, marginBottom: 8 }}>{expertObj.label || expertObj.description}</div>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: "rgba(243,239,230,0.85)" }}>{expertObj.description}</div>
        </div>
      )}

      {/* Rappel CTA */}
      <div style={{ background: "#fff", border: "1px solid rgba(21,41,79,0.1)", padding: "24px", borderRadius: 6, textAlign: "center", marginBottom: 28 }}>
        {callbackSent ? (
          <div data-testid="callback-confirmed" style={{ color: "var(--auprea-green)", fontSize: 15, lineHeight: 1.6 }}>
            <CheckCircle2 size={20} style={{ display: "inline-block", verticalAlign: "-4px", marginRight: 8 }} />
            Merci&nbsp;! Votre demande a bien été transmise à AUPRÉA. Un conseiller vous recontactera très prochainement pour votre point de situation de 15 minutes.
          </div>
        ) : (
          <>
            <button
              onClick={onRequestCallback}
              disabled={callbackSubmitting}
              data-testid="btn-request-callback"
              style={{
                padding: "14px 32px",
                background: "var(--auprea-gold)",
                color: "var(--auprea-navy)",
                border: "none",
                fontWeight: 700,
                fontSize: 15,
                borderRadius: 4,
                cursor: callbackSubmitting ? "wait" : "pointer",
                letterSpacing: "0.03em",
              }}
            >
              {callbackSubmitting ? "Envoi…" : "Je souhaite être recontacté(e) par AUPRÉA"}
            </button>
            <div style={{ marginTop: 10, fontSize: 13, color: "rgba(35,38,46,0.6)" }}>
              Entretien gratuit de 15 minutes, sans engagement.
            </div>
          </>
        )}
      </div>

      {/* CTA inscription */}
      <div style={{ background: "rgba(202,168,106,0.1)", border: "1px solid rgba(202,168,106,0.35)", padding: "22px 24px", borderRadius: 6, textAlign: "center" }} data-testid="bilan-cta-inscription">
        <div style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', color: "var(--auprea-navy)", fontSize: 20, fontWeight: 500, marginBottom: 8 }}>Allez plus loin : créez votre carnet succession</div>
        <div style={{ fontSize: 14, color: "rgba(35,38,46,0.7)", marginBottom: 16, lineHeight: 1.6 }}>
          Centralisez vos documents, désignez vos personnes de confiance, protégez ce qui compte — en toute confidentialité.
        </div>
        <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" data-testid="cta-inscription-final" style={{ display: "inline-block", padding: "12px 26px", background: "var(--auprea-navy)", color: "#f3efe6", textDecoration: "none", fontWeight: 600, fontSize: 14, borderRadius: 4, letterSpacing: "0.03em" }}>
          Créer mon compte gratuit
        </a>
      </div>
    </div>
  );
}

function PillarBar({ label, value }) {
  const pct = Math.round((value / 10) * 100);
  return (
    <div style={{ background: "#fff", border: "1px solid rgba(21,41,79,0.08)", padding: "16px 18px", borderRadius: 6 }}>
      <div style={{ fontSize: 12, color: "rgba(35,38,46,0.65)", marginBottom: 8, lineHeight: 1.4 }} dangerouslySetInnerHTML={{ __html: label }} />
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8 }}>
        <span style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', fontSize: 24, color: "var(--auprea-navy)", fontWeight: 500 }}>{value}</span>
        <span style={{ fontSize: 13, color: "rgba(35,38,46,0.5)" }}>/ 10</span>
      </div>
      <div style={{ height: 4, background: "rgba(21,41,79,0.08)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: "var(--auprea-gold)" }} />
      </div>
    </div>
  );
}
